import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // If returning from Emergent OAuth, skip /me — AuthCallback will handle
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Skip /me probe if we have no token — avoids 401 noise on marketing pages.
    const token = typeof window !== "undefined" ? localStorage.getItem("bloq_token") : null;
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
      localStorage.removeItem("bloq_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    if (data.token) localStorage.setItem("bloq_token", data.token);
    return data.user;
  };

  const signup = async (nameOrObject, email, password) => {
    // Accept either a legacy string (name) or an object { first_name, last_name }
    let payload;
    if (typeof nameOrObject === "string") {
      payload = { name: nameOrObject, email, password };
    } else {
      payload = { ...nameOrObject, email, password };
    }
    const { data } = await api.post("/auth/signup", payload);
    setUser(data.user);
    if (data.token) localStorage.setItem("bloq_token", data.token);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("bloq_token");
    setUser(null);
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, signup, logout, refreshUser, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { formatApiError };
